import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Checkbox,
  CircularProgress,
  Divider,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../config/firebase";

const isValidUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (err) {
    return false;
  }
};

const TechPrepShowcaseFormC = () => {
  const { showcaseId } = useParams();
  const recaptchaRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [docId, setDocId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    project_title: "",
    oversized_project: "no",
    electricity_required: "no",
    attending_in_person: "yes",
    video_presentation_url: "",
    team_member_1: "",
    team_member_2: "",
    team_member_3: "",
  });
  const [readOnlyData, setReadOnlyData] = useState({
    tech_prep_SOD: "",
    Instructor_ID: "",
    competitive_category: "",
  });

  const [captchaValue, setCaptchaValue] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const loadSubmission = async () => {
      if (!showcaseId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, "ShowcaseSubmissions"),
          where("showcase_ID_string", "==", showcaseId),
        );
        const snap = await getDocs(q);
        if (snap.empty) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const submissionDoc = snap.docs[0];
        const data = submissionDoc.data();
        setDocId(submissionDoc.id);
        setFormData({
          project_title: data.project_title || "",
          oversized_project: data.oversized_project || "no",
          electricity_required: data.electricity_required || "no",
          attending_in_person: data.attending_in_person || "yes",
          video_presentation_url: data.video_presentation_url || "",
          team_member_1: data.team_member_1 || "",
          team_member_2: data.team_member_2 || "",
          team_member_3: data.team_member_3 || "",
        });
        setReadOnlyData({
          tech_prep_SOD: data.tech_prep_SOD || "",
          Instructor_ID: data.Instructor_ID || "",
          competitive_category: data.competitive_category || "",
        });
      } catch (err) {
        console.error("Error loading showcase submission:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadSubmission();
  }, [showcaseId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "video_presentation_url" && errors.video_presentation_url) {
      setErrors((prev) => ({ ...prev, video_presentation_url: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.video_presentation_url.trim()) {
      newErrors.video_presentation_url = "Video Presentation URL is required.";
    } else if (!isValidUrl(formData.video_presentation_url.trim())) {
      newErrors.video_presentation_url =
        "Please enter a valid URL (e.g. https://youtube.com/...).";
    }
    if (!captchaValue)
      newErrors.captcha = "Please complete the CAPTCHA verification.";
    if (!confirmed)
      newErrors.confirm =
        "Please confirm you've reviewed the information above.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const docRef = doc(db, "ShowcaseSubmissions", docId);
      await updateDoc(docRef, {
        ...formData,
        video_presentation_url: formData.video_presentation_url.trim(),
        updated_stamp: new Date().toISOString(),
      });

      setSubmitted(true);
      setSnackbar({
        open: true,
        message: "Video Presentation URL submitted successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error submitting video presentation URL:", err);
      setSnackbar({
        open: true,
        message: "Error submitting. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box mt={8} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md">
        <Box mt={8}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Submission Not Found
            </Typography>
            <Typography variant="body1" align="center">
              We couldn't find a Tech Prep Showcase submission for this link.
              Please check the link from your email, or contact{" "}
              <a href="mailto:techprep@sinclair.edu">techprep@sinclair.edu</a>{" "}
              for assistance.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box mt={8} mb={4} display="flex" flexDirection="column" alignItems="center">
        <Paper elevation={3} sx={{ width: "100%", p: 4 }}>
          <Typography variant="h4" align="center" gutterBottom color="primary">
            Video Presentation Submission
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="textSecondary"
            paragraph
          >
            Review your team's information below, provide your video
            presentation URL, and submit.
          </Typography>

          {submitted ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Your Video Presentation URL has been submitted successfully.
              {formData.video_presentation_url && (
                <>
                  {" "}
                  <a
                    href={formData.video_presentation_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View video
                  </a>
                </>
              )}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <TextField
                  fullWidth
                  label="Project Title"
                  name="project_title"
                  value={formData.project_title}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 3,
                  }}
                >
                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">Oversized Project</FormLabel>
                    <RadioGroup
                      row
                      name="oversized_project"
                      value={formData.oversized_project}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">
                      Electricity Required
                    </FormLabel>
                    <RadioGroup
                      row
                      name="electricity_required"
                      value={formData.electricity_required}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset" disabled sx={{ flex: 1 }}>
                    <FormLabel component="legend">
                      Attending In Person
                    </FormLabel>
                    <RadioGroup
                      row
                      name="attending_in_person"
                      value={formData.attending_in_person}
                    >
                      <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio />} label="No" />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  required
                  label="Video Presentation URL"
                  name="video_presentation_url"
                  value={formData.video_presentation_url}
                  onChange={handleChange}
                  variant="outlined"
                  placeholder="https://..."
                  error={!!errors.video_presentation_url}
                  helperText={errors.video_presentation_url}
                />

                <Divider />

                <Alert severity="info">
                  Important: Teams must have 2-5 student members. Teamwork is
                  a key factor in judging.
                </Alert>

                <TextField
                  fullWidth
                  label="Team Member 1"
                  name="team_member_1"
                  value={formData.team_member_1}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Team Member 2"
                  name="team_member_2"
                  value={formData.team_member_2}
                  onChange={handleChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Team Member 3"
                  name="team_member_3"
                  value={formData.team_member_3}
                  onChange={handleChange}
                  variant="outlined"
                />

                <Divider />

                <TextField
                  fullWidth
                  label="Site / School"
                  value={readOnlyData.tech_prep_SOD}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  label="Instructor"
                  value={readOnlyData.Instructor_ID}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  label="Competitive Category"
                  value={readOnlyData.competitive_category}
                  variant="outlined"
                  disabled
                  InputProps={{ readOnly: true }}
                />

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={
                      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                      "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    }
                    onChange={(value) => {
                      setCaptchaValue(value);
                      if (errors.captcha) {
                        setErrors((prev) => ({ ...prev, captcha: "" }));
                      }
                    }}
                    onExpired={() => setCaptchaValue(null)}
                    onErrored={() => setCaptchaValue(null)}
                  />
                </Box>
                {errors.captcha && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {errors.captcha}
                  </Typography>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => {
                        setConfirmed(e.target.checked);
                        if (errors.confirm) {
                          setErrors((prev) => ({ ...prev, confirm: "" }));
                        }
                      }}
                    />
                  }
                  label="Check this box once you've reviewed all of the above and are ready to submit."
                />
                {errors.confirm && (
                  <Typography variant="caption" color="error">
                    {errors.confirm}
                  </Typography>
                )}

                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!confirmed || submitting}
                    sx={{ minWidth: 200, py: 1.5 }}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default TechPrepShowcaseFormC;
